// ═══════════════════════════════════════════════════════════════════
//  useSearchCapabilities — Tier-aware Search capability resolver.
// ═══════════════════════════════════════════════════════════════════
//
//  Mirrors useAtlasCapabilities. Three feature levels:
//    - canFilter      Live-filter-as-you-type (Free+; always on)
//    - canCanvas      `/search` Search Canvas page (Pro+)
//    - canProbe       Smart probing (find similar / trace path / save trail) (Max+)
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { canUse, type PlanTier } from '@/lib/planTiers';

export interface SearchCapabilities {
  /** Live-filter-as-you-type (dim non-matches, highlight matches). Always on. */
  canFilter: boolean;
  /** Inline dropdown showing top-N hits across all visible content. */
  canQuickSearch: boolean;
  /** `/search` Search Canvas page. Pro+. */
  canCanvas: boolean;
  /** Smart probing inside the canvas. Max+. */
  canProbe: boolean;

  /** Lowest tier that unlocks the canvas (for upsell copy). */
  canvasMinTier: PlanTier;
  /** Lowest tier that unlocks probing. */
  probeMinTier: PlanTier;
  /** Current tier. */
  currentTier: PlanTier;
}

export function useSearchCapabilities(): SearchCapabilities {
  const { tier, limits } = usePlan();
  return useMemo(() => {
    const canvas = canUse(limits, 'search-canvas');
    const probe = canUse(limits, 'search-probing');
    return {
      // Live filter is part of the FW reading experience — never gated.
      canFilter: true,
      // Quick-search uses the same index as canvas; if canvas is unlocked
      // we expose the dropdown; otherwise just current-page filter.
      canQuickSearch: canvas.ok,
      canCanvas: canvas.ok,
      canProbe: probe.ok,
      canvasMinTier: canvas.ok ? tier : (canvas.requiredTier ?? 'pro'),
      probeMinTier: probe.ok ? tier : (probe.requiredTier ?? 'max'),
      currentTier: tier,
    };
  }, [tier, limits]);
}
